import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["badge"]

  connect() {
    console.log('Notifications controller connected')
    
    // Initialize previous count as null to avoid false notification on first load
    this.previousCount = null
    this.isFirstCheck = true
    
    // Attach click handlers to initial notification items
    this.attachNotificationClickHandlers()
    
    // Get initial count
    this.checkForNewNotifications()
    
    // Poll for new notifications every 30 seconds
    this.pollInterval = setInterval(() => {
      this.checkForNewNotifications()
    }, 30000)

    // Listen for custom notification events
    document.addEventListener('notification:received', this.handleNewNotification.bind(this))
  }

  disconnect() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }
    document.removeEventListener('notification:received', this.handleNewNotification.bind(this))
  }

  async checkForNewNotifications() {
    try {
      console.log('Checking for new notifications...')
      const response = await fetch('/notifications/unread_count')
      const data = await response.json()
      const newCount = data.count
      console.log(`Previous count: ${this.previousCount}, New count: ${newCount}, First check: ${this.isFirstCheck}`)
      
      // On first check, just store the count without triggering notifications
      if (this.isFirstCheck) {
        this.previousCount = newCount
        this.isFirstCheck = false
        this.updateBadge(newCount)
        console.log(`First check complete. Stored count: ${newCount}`)
        return
      }
      
      // Check if we have new notifications (count increased)
      if (this.previousCount !== null && newCount > this.previousCount) {
        // New notification(s) received!
        const notificationDiff = newCount - this.previousCount
        console.log(`NEW NOTIFICATION! Diff: ${notificationDiff}`)
        this.showNotificationPopup(notificationDiff)
        this.playNotificationSound()
        
        // Reload the notification dropdown to show new notifications
        await this.reloadNotificationDropdown()
      }
      
      // Update badge and store current count
      this.updateBadge(newCount)
      this.previousCount = newCount
    } catch (error) {
      console.error('Error checking notifications:', error)
    }
  }

  updateBadge(count) {
    if (this.hasBadgeTarget) {
      if (count > 0) {
        this.badgeTarget.style.display = 'block'
      } else {
        this.badgeTarget.style.display = 'none'
      }
    } else if (count > 0) {
      // Create dot if it doesn't exist
      const bellIcon = this.element.querySelector('.fe-bell')
      if (bellIcon) {
        const dot = document.createElement('span')
        dot.className = 'notification-dot'
        dot.setAttribute('data-notifications-target', 'badge')
        dot.style.cssText = `
          position: absolute;
          top: 6px;
          right: 8px;
          width: 10px;
          height: 10px;
          background-color: #dc3545;
          border-radius: 50%;
          border: 2px solid white;
          animation: pulse 2s infinite;
        `
        bellIcon.parentElement.style.position = 'relative'
        bellIcon.parentElement.appendChild(dot)
      }
    }
  }

  async markAsRead(event) {
    event.preventDefault()
    const notificationId = event.currentTarget.dataset.notificationId
    
    if (!notificationId) {
      window.location.href = event.currentTarget.href
      return
    }

    try {
      await fetch(`/notifications/${notificationId}/mark_as_read`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json'
        }
      })

      // Update badge count
      await this.checkForNewNotifications()

      // Navigate to the notification URL
      window.location.href = event.currentTarget.href
    } catch (error) {
      console.error('Error marking notification as read:', error)
      // Navigate anyway
      window.location.href = event.currentTarget.href
    }
  }

  handleNewNotification(event) {
    const { message, type, count } = event.detail
    
    // Update badge (dot)
    this.updateBadge(count)
    
    // Show SweetAlert popup notification
    this.showNotificationPopup(count, message)
    
    // Play notification sound
    this.playNotificationSound()
  }

  showNotificationPopup(count, customMessage = null) {
    console.log(`showNotificationPopup called with count: ${count}, customMessage: ${customMessage}`)
    
    // Determine message based on count
    let message = customMessage
    if (!message) {
      if (count === 1) {
        message = document.querySelector('html').getAttribute('lang') === 'ar' 
          ? 'لديك إشعار جديد' 
          : 'You have a new notification'
      } else {
        message = document.querySelector('html').getAttribute('lang') === 'ar'
          ? `لديك ${count} إشعارات جديدة`
          : `You have ${count} new notifications`
      }
    }

    const title = document.querySelector('html').getAttribute('lang') === 'ar'
      ? 'إشعار جديد'
      : 'New Notification'

    console.log(`Swal available: ${!!window.Swal}, Title: ${title}, Message: ${message}`)

    // Use SweetAlert2 Toast at the bottom
    if (window.Swal) {
      const Toast = Swal.mixin({
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer)
          toast.addEventListener('mouseleave', Swal.resumeTimer)
        }
      })

      Toast.fire({
        icon: 'info',
        title: title,
        text: message
      })
      console.log('SweetAlert toast fired')
    } else {
      console.error('Swal is not available!')
    }
  }


  playNotificationSound() {
    try {
      // Use Web Audio API to generate a pleasant bell notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      
      // Create a bell-like sound with three tones (like a notification chime)
      const playTone = (frequency, startTime, duration, volume = 0.3) => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.frequency.value = frequency
        oscillator.type = 'sine'
        
        gainNode.gain.setValueAtTime(volume, startTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration)

        oscillator.start(startTime)
        oscillator.stop(startTime + duration)
      }

      // Create a pleasant three-tone bell sound (ding-ding-ding)
      const now = audioContext.currentTime
      playTone(880, now, 0.15, 0.25)        // First ding (A5)
      playTone(1046, now + 0.15, 0.15, 0.2) // Second ding (C6)
      playTone(1318, now + 0.3, 0.2, 0.15)  // Third ding (E6)
      
    } catch (error) {
      console.log('Could not play notification sound:', error)
    }
  }

  async reloadNotificationDropdown() {
    try {
      console.log('Reloading notification dropdown...')
      
      // Get current locale from URL or HTML lang attribute
      const urlParams = new URLSearchParams(window.location.search)
      const locale = urlParams.get('locale') || document.querySelector('html').getAttribute('lang') || 'ar'
      
      // Fetch updated notifications HTML partial
      const response = await fetch(`/notifications/dropdown?locale=${locale}`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        
        // Find the dropdown menu and update its content
        const dropdownMenu = this.element.querySelector('.notifications-menu')
        if (dropdownMenu) {
          dropdownMenu.innerHTML = html
          
          // Re-attach event listeners to new notification items
          this.attachNotificationClickHandlers()
          
          console.log('Notification dropdown reloaded successfully')
        } else {
          console.error('Could not find .notifications-menu element')
        }
      } else {
        console.error('Failed to reload notifications:', response.status)
      }
    } catch (error) {
      console.error('Error reloading notification dropdown:', error)
    }
  }

  attachNotificationClickHandlers() {
    // Find all notification items in the dropdown
    const notificationItems = this.element.querySelectorAll('.notification-item')
    
    notificationItems.forEach(item => {
      // Remove any existing click listeners to avoid duplicates
      const newItem = item.cloneNode(true)
      item.parentNode.replaceChild(newItem, item)
      
      // Add click handler
      newItem.addEventListener('click', async (event) => {
        event.preventDefault()
        const notificationId = newItem.dataset.notificationId
        const href = newItem.getAttribute('href')
        
        console.log('Notification clicked:', { notificationId, href })
        
        if (notificationId) {
          try {
            // Mark as read
            await fetch(`/notifications/${notificationId}/mark_as_read`, {
              method: 'POST',
              headers: {
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
                'Content-Type': 'application/json'
              }
            })
            console.log('Notification marked as read')
          } catch (error) {
            console.error('Error marking notification as read:', error)
          }
        }
        
        // Navigate to the notification URL
        if (href && href !== '#') {
          console.log('Navigating to:', href)
          window.location.href = href
        } else {
          console.error('No valid href found')
        }
      })
    })
    
    console.log(`Attached click handlers to ${notificationItems.length} notification items`)
  }
}

